/* eslint-disable no-underscore-dangle */
import React, { useCallback, useState } from 'react';
import styled from 'styled-components';

import {
  Modal,
  Input,
  ModalProps,
  UserSelect,
  Title,
  Spin,
  Button,
  message,
} from 'components';
import { Input as AntDInput, Form } from 'antd';
import { capitalize } from 'utils';
import { TeamController, GroupController } from 'api';
import errorReporter from '@app/errorReporter';
import isEmail from 'validator/es/lib/isEmail';
import isURL from 'validator/es/lib/isURL';
import { OrgBasicUser } from 'contexts';
import { GroupOrTeamMember } from '../util';
import { TeamGroupModalContext, ModalType } from './TeamGroupModalContext';
import { toGroupMember, toTeamMember } from './helpers';
import UserList from './UserList';
import useIsBranchAdmin from '../Hooks/useIsBranchAdmin';

export interface TeamGroupModalProps extends ModalProps {
  onCancel?: () => void;
  onSave?: () => void;
}

const API_CALL_MAP: Record<ModalType, (id: string) => Promise<any>> = {
  group: GroupController.get,
  team: TeamController.get,
};

const TRANSFORM_MAP: Record<ModalType, (user: any) => GroupOrTeamMember> = {
  group: toGroupMember,
  team: toTeamMember,
};

export const TeamGroupModal = ({
  onCancel,
  onSave,
  ...rest
}: TeamGroupModalProps) => {
  const {
    editId,
    working,
    modalVisible,
    type,
    mode,
    setInfo,
    info,
    save,
    closeModal,
  } = React.useContext(TeamGroupModalContext);

  const { isBranchAdmin, branchAdminCanEdit, authorizedLocations } =
    useIsBranchAdmin();

  const capitalType = capitalize(type);
  const capitalMode = capitalize(mode);

  const [teamName, setTeamName] = useState<string>('');
  const [teamMlsId, setTeamMlsId] = useState<string>('');
  const [teamEmail, setTeamEmail] = useState<string>('');
  const [teamWebsite, setTeamWebsite] = useState<string>('');
  const [teamPhone, setTeamPhone] = useState<string>('');
  const [users, setUsers] = useState<GroupOrTeamMember[]>([]);
  const [loading, setLoading] = useState(true);

  // For Team only. See TeamService.update
  const [usersToAdd, setUsersToAdd] = useState<string[]>([]);
  const [usersToRemove, setUsersToRemove] = useState<string[]>([]);

  const emailValidStatus = React.useMemo(() => {
    if (teamEmail === '') return '';
    return isEmail(teamEmail) ? '' : 'error';
  }, [teamEmail]);

  const websiteValidStatus = React.useMemo(() => {
    if (teamWebsite === '') return '';
    return isURL(teamWebsite) ? '' : 'error';
  }, [teamWebsite]);

  const [teamMlsIdValidStatus, setTeamMlsIdValidStatus] = useState<
    '' | 'error'
  >('');

  const isSaveDisabled = useCallback(
    () =>
      loading ||
      working ||
      !teamName ||
      !users.length ||
      emailValidStatus === 'error' ||
      websiteValidStatus === 'error' ||
      teamMlsIdValidStatus === 'error',
    [
      loading,
      working,
      teamName,
      users,
      emailValidStatus,
      websiteValidStatus,
      teamMlsIdValidStatus,
    ],
  );

  const handleSelectUser = (
    _idsToAdd: string[],
    agentsToAdd: OrgBasicUser[],
  ) => {
    const agentUser = agentsToAdd[0];
    if (isBranchAdmin && !authorizedLocations?.includes(agentUser.location))
      return; // Added as a precaution, these users should not display in the list
    setUsersToAdd(prevUsersToAdd => [...prevUsersToAdd, agentUser.id]);
    setUsersToRemove(prevUsersToRemove =>
      prevUsersToRemove.filter(userId => userId !== agentUser.id),
    );
    setUsers(prevUsers => [...prevUsers, TRANSFORM_MAP[type](agentUser)]);
  };

  const handleRemoveUser = (idToRemove: string) => {
    setUsersToRemove(prevUsersToRemove => [...prevUsersToRemove, idToRemove]);
    setUsersToAdd(prevUsersToAdd =>
      prevUsersToAdd.filter(userId => userId !== idToRemove),
    );
    setUsers(prevUsers => prevUsers.filter(user => user.id !== idToRemove));
  };

  const handleNameChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
    setTeamName(ev.currentTarget.value);
  };

  const _resetModal = () => {
    setTeamName('');
    setTeamMlsId('');
    setTeamEmail('');
    setTeamWebsite('');
    setTeamPhone('');
    setUsers([]);
    setUsersToAdd([]);
    setUsersToRemove([]);
    setInfo(undefined);
    setLoading(true);
    setTeamMlsIdValidStatus('');
  };

  const _handleCancel = () => {
    if (onCancel) onCancel();
    closeModal();
    _resetModal();
  };

  const _handleSave = async () => {
    const isTeamMlsIdDuplicate = teamMlsId
      ? await TeamController.doesTeamMlsIdExist(teamMlsId, info?.id)
      : false;
    if (isTeamMlsIdDuplicate) {
      setTeamMlsIdValidStatus('error');
      return message.error(
        'Failed to save: Team with the same Team MLS ID already exists.',
      );
    }
    await save({
      ...info,
      name: teamName,
      teamMlsId,
      email: teamEmail,
      website: teamWebsite,
      phone: teamPhone,
      users,
      ...(type === 'team' && {
        usersToAdd,
        usersToRemove,
        users: [],
      }),
    });
    if (onSave) onSave();
    _resetModal();
  };

  React.useEffect(() => {
    const fetchData = async () => {
      try {
        const result = await API_CALL_MAP[type](editId);

        setInfo(result);
        setTeamName(result.name);
        setUsers(result.users);
        setTeamMlsId(result.teamMlsId);
        setTeamEmail(result.email || '');
        setTeamWebsite(result.website || '');
        setTeamPhone(result.phone || '');
      } catch (err) {
        errorReporter.error(`Could not fetch ${type}`);
      }
      setLoading(false);
    };

    if (modalVisible) {
      if (!editId) setLoading(false);
      else fetchData();
    }
  }, [editId, type, modalVisible]);

  const selectedUserIds = users.map(({ id }) => id);
  return (
    <Modal
      title={`${capitalMode} ${capitalType}`}
      open={modalVisible}
      onCancel={_handleCancel}
      footer={[
        <Button key="Close" type="ghost" onClick={_handleCancel}>
          Close
        </Button>,
        <Button
          key="Save"
          type="primary"
          disabled={isSaveDisabled()}
          onClick={_handleSave}
        >
          {working ? 'Saving...' : 'Save'}
        </Button>,
      ]}
      {...rest}
    >
      {loading ? (
        <Spin center size="large" />
      ) : (
        <>
          {capitalType === 'Group' ? (
            <Input
              placeholder={`${capitalType} Name`}
              value={teamName}
              onChange={handleNameChange}
              required
            />
          ) : (
            <>
              <InputGroup>
                <Input
                  placeholder={`${capitalType} Name`}
                  value={teamName}
                  onChange={handleNameChange}
                  required
                  label="Team Name"
                  data-cy="team-input-name"
                />
              </InputGroup>
              <InputGroup>
                <Form.Item
                  validateStatus={teamMlsIdValidStatus}
                  help={
                    teamMlsIdValidStatus !== 'error'
                      ? ''
                      : 'Team MLS ID must be unique (this ID is already in use).'
                  }
                  style={{ marginBottom: '0' }}
                >
                  <Input
                    placeholder="Team MLS ID"
                    value={teamMlsId}
                    onChange={e => {
                      if (teamMlsIdValidStatus === 'error')
                        setTeamMlsIdValidStatus('');
                      setTeamMlsId(e.target.value);
                    }}
                    label="Team MLS ID"
                    data-cy="team-input-mls-id"
                  />
                </Form.Item>
              </InputGroup>
              <HorizontalLine />
              <div style={{ marginTop: '20px' }}>
                <Title level={4}>Team Details</Title>
                <Form.Item
                  validateStatus={emailValidStatus}
                  help={
                    emailValidStatus !== 'error'
                      ? ''
                      : 'Please provide a valid email.'
                  }
                  style={{ marginBottom: '0' }}
                >
                  <Input
                    placeholder="Team Email"
                    value={teamEmail}
                    type="email"
                    label="Team Email"
                    onChange={e => setTeamEmail(e.target.value)}
                    data-cy="team-input-email"
                  />
                </Form.Item>
                <Form.Item
                  validateStatus={websiteValidStatus}
                  help={
                    websiteValidStatus !== 'error'
                      ? ''
                      : 'Please provide a valid website address.'
                  }
                  style={{ marginBottom: '0' }}
                >
                  <Input
                    placeholder="Team Website"
                    value={teamWebsite}
                    type="url"
                    label="Team Website"
                    onChange={e => setTeamWebsite(e.target.value)}
                    data-cy="team-input-website"
                  />
                </Form.Item>
                <Form.Item style={{ marginBottom: '0' }}>
                  <Input
                    placeholder="Team Phone"
                    value={teamPhone}
                    label="Team Phone"
                    onChange={e => setTeamPhone(e.target.value)}
                    data-cy="team-input-phone"
                  />
                </Form.Item>
              </div>
              <HorizontalLine />
            </>
          )}

          <UsersContainer>
            <Title level={4}>Users</Title>
            <UserSelect
              showSearch
              value={[]}
              onChange={handleSelectUser}
              sorted
              placeholder="Search by name or MLS ID"
              getPopupContainer={node => node.parentElement}
              filter={user =>
                !selectedUserIds.includes(user.id) &&
                (!isBranchAdmin || branchAdminCanEdit(user))
              }
              locations={authorizedLocations!}
              dataCy="team-select-user"
            />
            <UserList
              userType={type}
              users={users.filter(
                user => !isBranchAdmin || branchAdminCanEdit(user),
              )}
              onClickRemove={handleRemoveUser}
            />
          </UsersContainer>
        </>
      )}
    </Modal>
  );
};

const UsersContainer = styled.div`
  margin-top: 20px;
`;
const InputGroup = styled(AntDInput.Group)`
  margin: 10px 0;
`;

const HorizontalLine = styled.hr`
  background-color: #f5f5f5;
  height: 1px;
  border: none;
  margin: 20px 0 0 0;
`;
